import { create } from 'zustand';
import { QRCodeData, DynamicQRCode, QRCodeCustomization } from '../types';

interface QRStore {
  // State
  qrCodes: QRCodeData[];
  dynamicQRCodes: DynamicQRCode[];
  currentQRCode: QRCodeData | null;
  customization: QRCodeCustomization;
  isLoading: boolean;
  error: string | null;

  // Actions
  setCurrentQRCode: (qrCode: QRCodeData | null) => void;
  updateCustomization: (customization: Partial<QRCodeCustomization>) => void;
  addQRCode: (qrCode: QRCodeData) => void;
  addDynamicQRCode: (dynamicQRCode: DynamicQRCode) => void;
  updateQRCode: (id: string, updates: Partial<QRCodeData>) => void;
  deleteQRCode: (id: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  resetCustomization: () => void;
}

const defaultCustomization: QRCodeCustomization = {
  size: 256,
  foregroundColor: '#000000',
  backgroundColor: '#ffffff',
  errorCorrectionLevel: 'M',
  marginSize: 4,
  logoSize: 64,
};

export const useQRStore = create<QRStore>((set, get) => ({
  // Initial state
  qrCodes: [],
  dynamicQRCodes: [],
  currentQRCode: null,
  customization: defaultCustomization,
  isLoading: false,
  error: null,

  // Actions
  setCurrentQRCode: (qrCode) => set({ currentQRCode: qrCode }),
  
  updateCustomization: (updates) => 
    set((state) => ({
      customization: { ...state.customization, ...updates }
    })),

  addQRCode: (qrCode) => 
    set((state) => ({
      qrCodes: [...state.qrCodes, { ...qrCode, id: Date.now().toString(), createdAt: new Date() }]
    })),

  addDynamicQRCode: (dynamicQRCode) => 
    set((state) => ({
      dynamicQRCodes: [...state.dynamicQRCodes, dynamicQRCode]
    })),

  updateQRCode: (id, updates) =>
    set((state) => ({
      qrCodes: state.qrCodes.map(qr => 
        qr.id === id ? { ...qr, ...updates } : qr
      )
    })),

  deleteQRCode: (id) =>
    set((state) => ({
      qrCodes: state.qrCodes.filter(qr => qr.id !== id),
      dynamicQRCodes: state.dynamicQRCodes.filter(qr => qr.id !== id)
    })),

  setLoading: (loading) => set({ isLoading: loading }),
  
  setError: (error) => set({ error }),
  
  resetCustomization: () => set({ customization: defaultCustomization }),
}));
